import React, { useRef } from 'react';
import { Grid } from '@mui/material';
import { jsPDF } from 'jspdf';
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import { formatDate, toDateFromParts } from 'utils/formatters';
import PdfHeader from 'components/PdfHeader';
import boxShadows from 'assets/theme/base/boxShadows';

const tipoDocumentos = [
    { id: 80, label: 'CUIT' },
    { id: 86, label: 'CUIL' },
    { id: 96, label: 'DNI' },
];

const estadoEmailLabels = {
    success: 'Enviado',
    failed: 'Error en envio',
    pending: 'Pendiente',
};

const EntregaPDF = ({ data, onGeneratePdf }) => {
    const documentsRef = useRef(null);
    const { lg } = boxShadows;

    const handleGeneratePdf = async () => {
        if (!data) return;

        const document = documentsRef.current.childNodes[0];
        const doc = new jsPDF({
            format: 'a4',
            unit: 'px',
            orientation: 'portrait',
            hotfixes: ['px_scaling'],
            compress: true,
        });

        await doc.html(document, {
            margin: [40, 0, 40, 0],
            callback(pdf) {
                pdf.save(`solicitud-entrega-${data.id}.pdf`);
                if (onGeneratePdf) onGeneratePdf();
            },
        });
    };

    React.useEffect(() => {
        if (data && typeof data === 'object') {
            handleGeneratePdf();
        }
    }, [data]);

    if (!data) return null;

    const getProveedorName = (proveedor) => {
        if (!proveedor || typeof proveedor !== 'object') return '';
        const parts = [];

        if (proveedor.razonSocial) {
            parts.push(proveedor.razonSocial);
        } else if (proveedor.nombre) {
            parts.push(proveedor.nombre);
        }

        if (proveedor.apellido) {
            parts.push(proveedor.apellido);
        }

        return parts.join(' ');
    };

    const detalles = Array.isArray(data?.detalles) ? data.detalles : [];
    const emailStatusLabel = estadoEmailLabels[data?.estadoEmail] ?? 'Pendiente';
    const provisionLabel = data?.provision?.id ? `OP #${data.provision.id}` : 'Sin asignar';

    const horaEntregaDate = toDateFromParts(data?.fechaEntrega, data?.horaEntrega);
    const horaEntregaLabel = data?.horaEntregaFin && data?.horaEntregaInicio ? `Entre las ${data.horaEntregaInicio} y las ${data.horaEntregaFin}` : (horaEntregaDate ? horaEntregaDate : 'N/A');

    const pliegoVencimientoDias = data?.provision?.pedidoCompra?.pliegoVencimientoDias;
    const pliegoParticularidades = data?.provision?.pedidoCompra?.pliegoParticularidades;
    const solicitante = data?.provision?.pedidoCompra?.createdUser
        ? `${data.provision.pedidoCompra.createdUser.nombre || ''} ${data.provision.pedidoCompra.createdUser.apellido || ''} (Tel ${data.provision.pedidoCompra.createdUser.telefono || 'N/A'})`.trim()
        : null;
    const provisionCreatedAtLabel = data?.provision?.createdAt
        ? formatDate(data.provision.createdAt, 'dd/MM/yyyy')
        : null;
    const hasPliegoVencimientoDias = pliegoVencimientoDias !== null && pliegoVencimientoDias !== undefined;
    const hasPliegoParticularidades = pliegoParticularidades !== null && pliegoParticularidades !== undefined;
    const showPliegoConditions = hasPliegoVencimientoDias || hasPliegoParticularidades;
    return (
        <SoftBox
            p={2}
            display='flex'
            alignItems='center'
            flexDirection='column'
            minHeight='100vh'
            height='100%'
            gap={4}
            style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}
        >
            <div ref={documentsRef}>
                <SoftBox
                    sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        width: '794px',
                        backgroundColor: '#ffffff',
                        boxShadow: lg,
                        px: 6,
                    }}
                    pb={5}
                >
                    <PdfHeader />

                    <SoftBox
                        mt={4}
                        display='flex'
                        flexDirection='column'
                        gap={2}
                        lineHeight={1.7}
                        fontSize='12px'
                    >
                        <SoftTypography variant='body1' fontWeight='bold' textAlign='center'>Solicitud de Entrega</SoftTypography>

                        <Grid
                            container
                            border='1pt solid black'
                            lineHeight={1.5}
                            sx={{ '& > *:not(:last-child)': { borderBottom: '1pt solid black' } }}
                        >
                            <Grid item xs={12} backgroundColor='#f0f0f0' fontWeight='bold' px={1}>
                                DATOS DEL PROVEEDOR
                            </Grid>

                            <Grid item xs={4} borderRight='1pt solid black' backgroundColor='#f0f0f0' fontWeight='bold' px={1}>
                                Nombre / Razon social
                            </Grid>
                            <Grid item xs={8} px={1}>
                                {getProveedorName(data?.proveedor) || 'N/A'}
                            </Grid>

                            <Grid item xs={4} borderRight='1pt solid black' backgroundColor='#f0f0f0' fontWeight='bold' px={1}>
                                Tipo de documento
                            </Grid>
                            <Grid item xs={8} px={1}>
                                {tipoDocumentos.find((item) => String(item.id) === String(data?.proveedor?.tipoDocumento))?.label ?? 'Documento'}
                            </Grid>

                            <Grid item xs={4} borderRight='1pt solid black' backgroundColor='#f0f0f0' fontWeight='bold' px={1}>
                                Documento
                            </Grid>
                            <Grid item xs={8} px={1}>
                                {data?.proveedor?.documento ?? '-'}
                            </Grid>

                            <Grid item xs={4} borderRight='1pt solid black' backgroundColor='#f0f0f0' fontWeight='bold' px={1}>
                                Correo electronico
                            </Grid>
                            <Grid item xs={8} px={1}>
                                {data?.proveedor?.user?.email ?? '-'}
                            </Grid>
                        </Grid>

                        <Grid
                            container
                            border='1pt solid black'
                            lineHeight={1.5}
                            sx={{ '& > *:not(:last-child)': { borderBottom: '1pt solid black' } }}
                        >
                            <Grid item xs={4} borderRight='1pt solid black' backgroundColor='#f0f0f0' fontWeight='bold' px={1}>
                                Fecha de entrega
                            </Grid>
                            <Grid item xs={8} px={1}>
                                {data?.fechaEntrega ? formatDate(data.fechaEntrega, 'dd/MM/yyyy') : 'N/A'}
                            </Grid>

                            <Grid item xs={4} borderRight='1pt solid black' backgroundColor='#f0f0f0' fontWeight='bold' px={1}>
                                Hora de entrega
                            </Grid>
                            <Grid item xs={8} px={1}>
                                {horaEntregaLabel}
                            </Grid>

                            <Grid item xs={4} borderRight='1pt solid black' backgroundColor='#f0f0f0' fontWeight='bold' px={1}>
                                Orden de provision
                            </Grid>
                            <Grid item xs={8} px={1}>
                                {provisionLabel}
                            </Grid>

                            <Grid item xs={4} borderRight='1pt solid black' backgroundColor='#f0f0f0' fontWeight='bold' px={1}>
                                Productos incluidos
                            </Grid>
                            <Grid item xs={8} px={1}>
                                {detalles.length}
                            </Grid>
                        </Grid>

                        <Grid
                            container
                            border='1pt solid black'
                            sx={{
                                '& > .MuiGrid-item:not(:last-child)': { borderBottom: '1pt solid black' }
                            }}
                        >
                            <Grid item xs={12} backgroundColor='#f0f0f0' fontWeight='bold' px={1}>
                                PRODUCTOS A ENTREGAR
                            </Grid>

                            <Grid
                                item
                                container
                                xs={12}
                                backgroundColor='#f0f0f0'
                                fontWeight='bold'
                                sx={{ '& > *:not(:last-child)': { borderRight: '1pt solid black' } }}
                            >
                                <Grid item xs={1} px={0.5} textAlign='center' fontSize='10px'>
                                    Item
                                </Grid>
                                <Grid item xs={3} px={0.5} fontSize='10px'>
                                    Descripcion
                                </Grid>
                                <Grid item xs={1.5} px={0.5} textAlign='center' fontSize='10px'>
                                    Cant. solicitada
                                </Grid>
                                <Grid item xs={1} px={0.5} textAlign='center' fontSize='10px'>
                                    Unidad
                                </Grid>
                                <Grid item xs={1.5} px={0.5} textAlign='center' fontSize='10px'>
                                    Cant. recibida
                                </Grid>
                                <Grid item xs={2} px={0.5} textAlign='center' fontSize='10px'>
                                    Fecha recepcion
                                </Grid>
                                <Grid item xs={2} px={0.5} textAlign='center' fontSize='10px'>
                                    Observacion
                                </Grid>
                            </Grid>

                            {detalles.length > 0 ? (
                                detalles.map((detalle, index) => (
                                    <Grid
                                        key={index}
                                        item
                                        container
                                        xs={12}
                                        lineHeight={1.5}
                                        sx={{ '& > *:not(:last-child)': { borderRight: '1pt solid black' } }}
                                    >
                                        <Grid item xs={1} px={0.5} textAlign='center' fontSize='10px'>
                                            {index + 1}
                                        </Grid>
                                        <Grid item xs={3} px={0.5} fontSize='10px'>
                                            {detalle?.nombre || 'N/A'}
                                        </Grid>
                                        <Grid item xs={1.5} px={0.5} textAlign='center' fontSize='10px'>
                                            {detalle?.cantidad ?? '-'}
                                        </Grid>
                                        <Grid item xs={1} px={0.5} textAlign='center' fontSize='10px'>
                                            {detalle?.unidad ?? '-'}
                                        </Grid>
                                        <Grid item xs={1.5} px={0.5} textAlign='center' fontSize='10px'>
                                            {' '}
                                        </Grid>
                                        <Grid item xs={2} px={0.5} textAlign='center' fontSize='10px'>
                                            {' '}
                                        </Grid>
                                        <Grid item xs={2} px={0.5} textAlign='center' fontSize='10px'>
                                            {' '}
                                        </Grid>
                                    </Grid>
                                ))
                            ) : (
                                <Grid item xs={12} px={1} py={2} textAlign='center'>
                                    No se registran productos asociados a la solicitud.
                                </Grid>
                            )}
                        </Grid>

                        {data?.observaciones && (
                            <SoftBox border='1pt solid black' px={1} py={2}>
                                <SoftTypography variant='h6' fontWeight='bold' mb={1}>
                                    Observaciones
                                </SoftTypography>
                                <SoftTypography variant='caption' lineHeight={1.5}>
                                    {data.observaciones}
                                </SoftTypography>
                            </SoftBox>
                        )}

                        {showPliegoConditions && (
                            <SoftBox border='1pt solid black' px={1} py={2}>
                                <SoftTypography variant='h6' fontWeight='bold' mb={1}>
                                    Condiciones del pliego particular
                                </SoftTypography>
                                <SoftBox display='flex' flexDirection='column' gap={0.5}>
                                    {hasPliegoVencimientoDias && (
                                        <SoftTypography variant='caption' lineHeight={1.5}>
                                            {`La fecha de vencimiento del producto debe estar comprendida dentro de los proximos ${pliegoVencimientoDias} dias a partir de ${provisionCreatedAtLabel || 'N/A'}.`}
                                        </SoftTypography>
                                    )}
                                    {hasPliegoParticularidades && (
                                        <SoftTypography variant='caption' lineHeight={1.5}>
                                            {`Particularidades: ${pliegoParticularidades}`}
                                        </SoftTypography>
                                    )}
                                </SoftBox>
                                <SoftTypography variant='text' mt={2} display='block'>
                                    Ante cualquier duda o consulta, por favor comuníquese con el solicitante <strong>{solicitante}</strong>.
                                </SoftTypography>
                            </SoftBox>
                        )}
                        <SoftTypography variant='text' textAlign='center' display='block' mt={2}>
                            Este documento se genero el {formatDate(new Date(), 'dd/MM/yyyy hh:mm')} hs. por {data.user?.nombre || 'N/A'} {data.user?.apellido || ''} ({data.user?.email || 'N/A'}).
                        </SoftTypography>
                    </SoftBox>
                </SoftBox>
            </div>
        </SoftBox>
    );
};

export default EntregaPDF;




